import {
  isLevelSignal,
  type VisualSignalFrame,
  type VisualSignalName,
  isVisualSignalName,
} from '../signals/VisualSignal.js';
import type { BlendMode, ResolvedLayerState } from '../core/VisualRenderer.js';
import { type MutationAction, type VisualMutation, mutation } from '../core/VisualMutation.js';

/**
 * One renderer, configured, in a stack of them.
 *
 * A layer is data. It says which renderer draws it, how loudly, where in the
 * stack, and — the part that makes this an art engine rather than an overlay
 * system — how signals reach that renderer's parameters.
 *
 * The mapping is why `SUMI` can read `BEAT` as an ink drop while `MATH` reads
 * the same `BEAT` as rotation. Neither renderer decides that; the recipe does.
 * A renderer declares parameters it understands and the mapping decides what
 * feeds them, so the same renderer in two layers can behave nothing alike.
 */

/** How one signal reaches one parameter. */
export interface SignalMappingEntry {
  /** The renderer parameter this drives. */
  readonly target: string;
  readonly scale?: number;
  readonly offset?: number;
  /**
   * Shapes the signal before scaling.
   *
   * `sqrt` is here for the unbounded counters: `COMBO` mapped linearly makes the
   * first ten notes invisible and the next hundred indistinguishable.
   */
  readonly curve?: 'linear' | 'sqrt' | 'square' | 'log';
  readonly clamp?: readonly [number, number];
}

/**
 * How one signal becomes an action.
 *
 * The Phase 3 form. `strength` is the force of a one-shot; `scale` multiplies a
 * level signal's value for a sustained one. Both are optional because the
 * common case — "a miss distorts things" — should read as one line of JSON.
 */
export interface SignalMutationEntry {
  readonly action: MutationAction;
  readonly strength?: number;
  readonly scale?: number;
  readonly curve?: SignalMappingEntry['curve'];
}

/**
 * The three forms a mapping may take.
 *
 * All of them still parse: `"BEAT": "rotation"` from Phase 2 keeps working, and
 * so does the object form with `target`. A recipe written before Phase 3 loads
 * unchanged, which is what §4's backward-compatibility requirement means in
 * practice — the schema version does not move because nothing was taken away.
 */
export type SignalMappingValue = string | SignalMappingEntry | SignalMutationEntry;

export function isMutationEntry(
  value: SignalMappingEntry | SignalMutationEntry,
): value is SignalMutationEntry {
  return typeof (value as SignalMutationEntry).action === 'string';
}

export type SignalMapping = Readonly<Partial<Record<VisualSignalName, SignalMappingValue>>>;

export interface VisualLayerConfig {
  readonly id: string;
  readonly renderer: string;
  readonly enabled: boolean;
  readonly opacity: number;
  readonly blendMode: BlendMode;
  readonly intensity: number;
  readonly seed: number;
  readonly audioReactivity: number;
  readonly zDepth: number;
  readonly parameters: Readonly<Record<string, number | string | boolean>>;
  readonly signalMapping: SignalMapping;
}

/**
 * Applies a layer's mapping to a frame of signals.
 *
 * Parameters start as the recipe's static values and are overwritten by anything
 * a mapping computes, so a mapped parameter wins over its authored default and
 * an unmapped one keeps it. `audioReactivity` scales only what the mapping
 * produced — it is a depth control for how much the music moves this layer, and
 * turning it to zero must leave the layer sitting at its authored values rather
 * than at zero.
 */
export function resolveLayer(
  config: VisualLayerConfig,
  signals: VisualSignalFrame,
): ResolvedLayerState {
  const parameters: Record<string, number | string | boolean> = { ...config.parameters };

  for (const key of Object.keys(config.signalMapping)) {
    if (!isVisualSignalName(key)) continue;
    const mapping = normaliseMapping(config.signalMapping[key]);
    // Action mappings do not write parameters; they are dispatched separately,
    // by `sustainedMutations` and by the engine's event route.
    if (mapping === null || isMutationEntry(mapping)) continue;

    const raw = signals[key];
    const shaped = applyCurve(raw, mapping.curve ?? 'linear');
    const scaled = shaped * (mapping.scale ?? 1) + (mapping.offset ?? 0);

    const authored = config.parameters[mapping.target];
    const base = typeof authored === 'number' ? authored : 0;
    // Reactivity blends between "as authored" and "as the music says".
    const blended = base + (scaled - base) * config.audioReactivity;

    parameters[mapping.target] = mapping.clamp === undefined
      ? blended
      : clamp(blended, mapping.clamp[0], mapping.clamp[1]);
  }

  return {
    id: config.id,
    opacity: config.opacity,
    intensity: config.intensity,
    zDepth: config.zDepth,
    blendMode: config.blendMode,
    audioReactivity: config.audioReactivity,
    parameters,
  };
}

export function normaliseMapping(
  value: SignalMappingValue | undefined,
): SignalMappingEntry | SignalMutationEntry | null {
  if (value === undefined) return null;
  if (typeof value === 'string') return value.length === 0 ? null : { target: value };
  if (isMutationEntry(value)) return value;
  return typeof value.target === 'string' && value.target.length > 0 ? value : null;
}

/**
 * The mutations a layer asks for continuously, this frame.
 *
 * Only level signals produce these. An event signal mapped to an action fires
 * once, when the event does, and goes out through the engine's signal route
 * instead — firing it every frame as well would apply a miss sixty times.
 */
export function sustainedMutations(
  config: VisualLayerConfig,
  signals: VisualSignalFrame,
  time: number,
): VisualMutation[] {
  const out: VisualMutation[] = [];
  for (const key of Object.keys(config.signalMapping)) {
    if (!isVisualSignalName(key) || !isLevelSignal(key)) continue;
    const entry = normaliseMapping(config.signalMapping[key]);
    if (entry === null || !isMutationEntry(entry)) continue;

    const shaped = applyCurve(signals[key], entry.curve ?? 'linear');
    const strength = shaped * (entry.scale ?? 1) * config.audioReactivity;
    if (strength === 0) continue;
    out.push(mutation(entry.action, strength, key, time, { sustained: true }));
  }
  return out;
}

/**
 * The mutation an event should fire on this layer, if it maps to one.
 *
 * Returns null when the layer does not map that signal to an action, which is
 * the usual case — most layers care about two or three signals.
 */
export function eventMutation(
  config: VisualLayerConfig,
  signal: VisualSignalName,
  eventStrength: number,
  time: number,
  detail: Readonly<Record<string, number | string | boolean>>,
): VisualMutation | null {
  const entry = normaliseMapping(config.signalMapping[signal]);
  if (entry === null || !isMutationEntry(entry)) return null;
  const strength = (entry.strength ?? eventStrength) * config.audioReactivity;
  return mutation(entry.action, strength, signal, time, { detail });
}

export type { MutationAction };

function applyCurve(value: number, curve: NonNullable<SignalMappingEntry['curve']>): number {
  switch (curve) {
    case 'sqrt':
      return value <= 0 ? 0 : Math.sqrt(value);
    case 'square':
      return value * value;
    case 'log':
      return value <= 0 ? 0 : Math.log1p(value);
    default:
      return value;
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
